import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { applyStoreMeta } from "@/lib/storeMeta";

const StoreCtx = createContext(null);
const DEFAULT_C = { primary: "#1d4ed8", accent: "#f97316", bg: "#f8fafc", surface: "#ffffff", text: "#0f172a", muted: "#64748b" };

export function StoreProvider({ slug, children }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    api.get(`/store/${slug}`)
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((e) => setState({ loading: false, error: apiErr(e.response?.data?.detail), data: null }));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const metaStore = state.data?.store;
  useEffect(() => (metaStore ? applyStoreMeta(metaStore) : undefined),
    [metaStore?.name, metaStore?.tagline, metaStore?.favicon, metaStore?.logo]); // eslint-disable-line react-hooks/exhaustive-deps

  const theme = state.data?.store?.theme;
  // the owner's own primary / secondary (theme accent) colours sit on top of the theme's palette
  const own = state.data?.store?.theme_colors || {};
  const hex = (v) => (/^#[0-9a-f]{6}$/i.test(v || "") ? v : null);
  const c = { ...(theme?.colors || DEFAULT_C), ...(hex(own.primary) && { primary: own.primary }), ...(hex(own.accent) && { accent: own.accent }) };
  const dark = theme?.mode === "dark";
  const value = {
    ...state, slug, reload: load,
    store: state.data?.store || null,
    products: state.data?.products || [],
    categories: state.data?.categories || [],
    coupons: state.data?.coupons || [],
    homepage: state.data?.homepage || { configured: false },
    theme, c, font: theme?.font || "Inter", dark,
    border: dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
    muted: c.muted || (dark ? "#94a3b8" : "#64748b"),
    cardShadow: dark ? "none" : "0 6px 24px rgba(0,0,0,0.06)",
  };
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export const useStore = () => useContext(StoreCtx);
